import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export function useDeleteFinalCircuitMutation({
    electricalProjectVersionId,
}: {
    electricalProjectVersionId: string;
}) {
    const { t } = useTranslation('re-inspections');

    const deleteFinalCircuitMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/finalcircuits/delete',
        {
            onMutate: async (variable) => {
                const finalCircuitListQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
                    electricalProjectVersionId,
                });
                const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId,
                });

                const previousDataList = queryClient.getQueryData(finalCircuitListQueryOptions.queryKey) as components['schemas']['ListElectricalProjectFinalCircuitQueryResult'];

                await queryClient.cancelQueries(summaryOptions);
                await queryClient.cancelQueries(finalCircuitListQueryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectFinalCircuitQueryResult']>(
                    finalCircuitListQueryOptions.queryKey,
                    (old) => {
                        if (old) {
                            const updatedData: components['schemas']['ListElectricalProjectFinalCircuitQueryResult'] = {
                                ...old,
                                FinalCircuits: old.FinalCircuits.filter(
                                    x => x.ElectricalProjectFinalCircuitId !== variable.params.query.ElectricalProjectFinalCircuitId,
                                ),
                            };
                            return updatedData;
                        }
                        return old;
                    },
                );

                queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(
                    summaryOptions.queryKey,
                    (old) => {
                        if (old && old.Summary) {
                            return {
                                ...old,
                                Summary: {
                                    ...old.Summary,
                                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                                    FinalCircuitCount: (old.Summary.FinalCircuitCount || 1) - 1,
                                },
                            };
                        }
                        return old;
                    },
                );

                return { previousDataList };
            },
            onSuccess: () => {
                toast.success(t('finalCircuits.messages.deleted'));
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
                        electricalProjectVersionId,
                    }),
                );
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    async function handleDelete({
        electricalProjectFinalCircuitId,
    }: {
        electricalProjectFinalCircuitId: string;
    }) {
        if (await showConfirm(t('finalCircuits.messages.confirmDelete'))) {
            deleteFinalCircuitMutation.mutate({
                params: {
                    query: {
                        ElectricalProjectFinalCircuitId:
                            electricalProjectFinalCircuitId,
                    },
                },
            });
        }
    }

    return {
        deleteFinalCircuitMutation,
        handleDelete,
    };
}
